/**
 * useTheme.ts
 * Light/dark theme toggle — module-level singleton (mirrors lib/session.ts's
 * reactive-singleton pattern), since the theme is one global piece of state,
 * not per-component.
 *
 * The ONLY signal every token/utility reacts to is the `.dark` class on
 * <html> (style.css defines a full dark token palette under `.dark` and
 * `@custom-variant dark` for any `dark:` utility escape hatch). Persisted to
 * localStorage; falls back to the OS `prefers-color-scheme` on first visit.
 */
import { ref } from 'vue';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'suite-theme';

function resolveInitialTheme(): ThemeMode {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  // jsdom (component tests that don't explicitly test theming) doesn't implement
  // matchMedia — treat "unsupported" the same as "no preference" (default light).
  if (typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeClass(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', mode === 'dark');
}

const theme = ref<ThemeMode>(resolveInitialTheme());
applyThemeClass(theme.value);

export interface UseThemeReturn {
  theme: typeof theme;
  setTheme(mode: ThemeMode): void;
  toggleTheme(): void;
}

export function useTheme(): UseThemeReturn {
  function setTheme(mode: ThemeMode): void {
    theme.value = mode;
    applyThemeClass(mode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }

  function toggleTheme(): void {
    setTheme(theme.value === 'dark' ? 'light' : 'dark');
  }

  return { theme, setTheme, toggleTheme };
}

/**
 * Only for tests — resets the module-level `theme` singleton back to
 * `resolveInitialTheme()` and re-applies the `.dark` class. `theme` is
 * resolved once at module load; under `vitest run --no-isolate` the module
 * loads once per WORKER (not per spec file), so a test in one file calling
 * `setTheme('dark')` left `document.documentElement` and the singleton ref
 * dark for every later file in that worker. Never call from app code.
 *
 * Safe to call unconditionally from a global test hook even for specs that
 * run in the plain `node` environment (no `window`/`document`) — this module
 * is only ever imported by DOM-facing shell components, but the global hook
 * doesn't know that, so this no-ops rather than throwing when there's no DOM.
 */
export function __resetThemeForTests(): void {
  if (typeof window === 'undefined') return;
  theme.value = resolveInitialTheme();
  applyThemeClass(theme.value);
}
